import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, getToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AddPropertyDialog from "@/components/AddPropertyDialog";
import ApprovalsDialog from "@/components/ApprovalsDialog";
import OverdueAlerts from "@/components/OverdueAlerts";
import Logo from "@/components/Logo";
import { Building2, Plus, LogOut, ChevronRight, User, Eye, ShieldCheck, CalendarDays, TrendingUp, Sparkles, Mail } from "lucide-react";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const today = () =>
  new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const COVERS = [
  "https://images.unsplash.com/photo-1775138260921-883455954eb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwYXBhcnRtZW50JTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3NzcxMDA5NTl8MA&ixlib=rb-4.1.0&q=85&w=600",
  "https://images.unsplash.com/photo-1759744881663-71a15b53aaa0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwYXBhcnRtZW50JTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3NzcxMDA5NTl8MA&ixlib=rb-4.1.0&q=85&w=600",
  "https://images.unsplash.com/photo-1769890893164-8737168ed463?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwYXBhcnRtZW50JTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3NzcxMDA5NTl8MA&ixlib=rb-4.1.0&q=85&w=600",
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchPending = async () => {
    if (!isAdmin) return;
    try {
      const [u, r] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/password-resets"),
      ]);
      const pendingUsers = u.data.filter((x) => !x.is_approved).length;
      setPendingCount(pendingUsers + r.data.length);
    } catch {
      /* ignore */
    }
  };

  const fetchProps = async () => {
    try {
      const { data } = await api.get("/properties");
      setProperties(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProps();
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (payload) => {
    try {
      await api.post("/properties", payload);
      toast.success("Property added");
      setOpen(false);
      fetchProps();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Top bar */}
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size={52} showWordmark wordmarkClassName="text-stone-900 text-xl sm:text-2xl" />
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Button variant="ghost" size="sm" onClick={() => setApprovalsOpen(true)} className="text-stone-700 hover:text-emerald-900 relative" data-testid="approvals-button">
                <ShieldCheck className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                Users
                {pendingCount > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold" data-testid="pending-count">
                    {pendingCount}
                  </span>
                )}
              </Button>
            )}
            {!isAdmin && (
              <Badge variant="outline" className="hidden sm:flex border-stone-200 text-stone-600 gap-1 rounded-full" data-testid="viewer-badge">
                <Eye className="h-3 w-3" strokeWidth={1.5} />
                View only
              </Badge>
            )}
            <div className="hidden sm:flex items-center gap-2 text-sm text-stone-600">
              <User className="h-4 w-4" strokeWidth={1.5} />
              {user?.name || user?.email}
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-stone-600 hover:text-emerald-900" data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 sm:py-12">
        {/* Hero */}
        <section className="relative mb-10 overflow-hidden rounded-2xl border border-stone-200 bg-gradient-to-br from-emerald-950 via-emerald-900 to-stone-900 text-stone-50" data-testid="dashboard-hero">
          {/* Decorative layers */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-16 -bottom-24 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-8 p-8 sm:p-10">
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.2em] text-emerald-100 backdrop-blur">
                <Sparkles className="h-3 w-3" strokeWidth={1.5} />
                {today()}
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                {greeting()},
                <br />
                <span className="font-normal text-emerald-100">{user?.name || "there"}.</span>
              </h1>
              <p className="mt-4 max-w-md text-stone-300 text-base">
                Your rental portfolio at a glance — documents, rent, GST and tenants, all in one quiet place.
              </p>
            </div>

            {/* Stat tile */}
            <div className="lg:col-span-2 self-center">
              <div className="rounded-xl border border-white/10 bg-white/[0.06] backdrop-blur p-7" data-testid="stat-properties">
                <div className="flex items-center gap-2 text-emerald-200/80 text-base font-semibold uppercase tracking-[0.18em]">
                  <Building2 className="h-5 w-5" strokeWidth={1.5} />
                  Properties
                </div>
                <div className="mt-3 text-5xl sm:text-6xl font-light tracking-tight">{properties.length}</div>
                <div className="mt-2 text-base text-stone-300">Under management</div>
              </div>
            </div>
          </div>
        </section>

        {/* Properties section header */}
        <OverdueAlerts />
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-2 flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.5} />
              Your portfolio
            </p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-stone-900">Properties</h2>
            <p className="mt-1 text-sm text-stone-600">Tap a property to manage its documents, rent and contacts.</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/send-digest?force=true");
                    if (data.sent) {
                      toast.success(`Digest sent to ${data.recipients.length} recipient${data.recipients.length > 1 ? "s" : ""}`);
                    } else {
                      toast.info(data.reason === "no_recipients" ? "No recipients found" : "Digest could not be sent");
                    }
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail));
                  }
                }}
                className="border-stone-200 text-stone-700 hover:bg-stone-100 rounded-md h-11 px-4"
                data-testid="send-digest-button"
                title="Send the daily digest to all admins right now"
              >
                <Mail className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Send digest now
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/generate-month");
                    if (data.created > 0) {
                      toast.success(`Generated ${data.created} new rent record${data.created > 1 ? "s" : ""} for ${data.target}`);
                      fetchProps();
                    } else {
                      toast.info(`All ${data.skipped} properties already have a record for ${data.target}`);
                    }
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail));
                  }
                }}
                className="border-stone-200 text-stone-700 hover:bg-stone-100 rounded-md h-11 px-4"
                data-testid="generate-month-button"
              >
                <CalendarDays className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Generate this month
              </Button>
              <Button
                onClick={() => setOpen(true)}
                className="bg-emerald-900 hover:bg-emerald-800 text-stone-50 rounded-md h-11 px-5"
                data-testid="add-property-button"
              >
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Add property
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-64 rounded-xl bg-white border border-stone-200 animate-pulse" />
            ))}
          </div>
        ) : properties.length === 0 ? (
          <Card className="border-stone-200 bg-white p-16 text-center rounded-xl">
            <Building2 className="h-10 w-10 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="text-xl font-medium text-stone-900">No properties yet</h3>
            <p className="text-stone-500 mt-2 mb-6 max-w-sm mx-auto">
              {isAdmin
                ? "Add your first rental and start tracking documents, rent payments and tenant contacts."
                : "There are no properties to view yet. Check back later."}
            </p>
            {isAdmin && (
              <Button onClick={() => setOpen(true)} className="bg-emerald-900 hover:bg-emerald-800 text-stone-50" data-testid="empty-add-button">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Add property
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((p, i) => (
              <button
                key={p.id}
                onClick={() => navigate(`/property/${p.id}`)}
                className="group text-left bg-white border border-stone-200 rounded-xl shadow-sm hover:shadow-md hover:border-stone-300 transition-all duration-200 overflow-hidden"
                data-testid={`property-card-${p.id}`}
              >
                <div
                  className="h-40 bg-stone-100 bg-center bg-cover"
                  style={{
                    backgroundImage: `url(${
                      p.has_cover
                        ? `${API}/properties/${p.id}/cover?auth=${encodeURIComponent(getToken() || "")}&v=${encodeURIComponent(p.cover_updated_at || p.created_at || "")}`
                        : COVERS[i % COVERS.length]
                    })`,
                  }}
                />
                <div className="p-6">
                  <h3 className="text-xl font-medium text-stone-900 group-hover:text-emerald-900 transition-colors">
                    {p.name}
                  </h3>
                  <p className="text-base text-stone-500 mt-2">
                    Tenant: <span className="text-stone-800 font-medium">{p.tenant_name || "—"}</span>
                  </p>
                  <div className="mt-6 flex items-center text-sm text-emerald-900">
                    Open
                    <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      <AddPropertyDialog open={open} onOpenChange={setOpen} onSubmit={handleCreate} />
      <ApprovalsDialog open={approvalsOpen} onOpenChange={setApprovalsOpen} onChange={fetchPending} />
    </div>
  );
}
